import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthContext } from "@/features/authentication/useAuthContext";
import { invalidateRecipeQueries } from "@/lib/recipeCacheKeys";
import { notifyError } from "@/lib/toast";
import { todayLocalDate } from "@/lib/dates";
import { mapDatabaseLogToRecipeLog, type RecipeLog } from "@/features/RecipeLog/types";

interface LogRecipeVars {
    recipeId: number;
}

/**
 * Log that a recipe was made today. Upserts on (recipe_id, made_on) so clicking
 * "made it" multiple times in one day resolves to a single row — a day counts
 * once. Returns the row (including any existing note) so the panel can seed its
 * note field. A DB trigger flips recipes.has_made_it on the first insert.
 */
export function useLogRecipe() {
    const queryClient = useQueryClient();
    const { user } = useAuthContext();

    return useMutation<RecipeLog, Error, LogRecipeVars>({
        mutationFn: async ({ recipeId }) => {
            const { data, error } = await supabase
                .from("recipe_logs")
                .upsert(
                    { recipe_id: recipeId, user_id: user!.id, made_on: todayLocalDate() },
                    { onConflict: "recipe_id,made_on", ignoreDuplicates: false }
                )
                .select()
                .single();

            if (error) throw error;
            return mapDatabaseLogToRecipeLog(data);
        },
        onError: () => {
            notifyError("Your entry wasn't saved. Try again.");
        },
        onSettled: (_data, _err, { recipeId }) => {
            invalidateRecipeQueries(queryClient, recipeId);
        }
    });
}
